import React, {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import {useSelector} from "react-redux";
import {currencyFormat} from "../../../utils/masks";
import {getBrokerLabel} from "../../../utils/broker";
import {useTranslation} from "react-i18next";
import AlertDelete from "../../../components/Alert/AlertDelete";
import {getBanks, setDefaultBank} from "../../../services/Bank";
import {FaCoins, FaHourglassStart, FaPercent, FaStore, FaWallet} from "react-icons/fa";
import DivDiagonal from "../../../components/DivDiagonal";
import {
    StatsIntro,
    StatStack,
    StatItem,
    StatIconWrap,
    StatText,
    StatLabel,
    StatHint,
    StatValue,
} from "../../../components/StatSummaryCard";
import {
    TradingWrapper,
    ContentArea,
    PageHeader as TradingPageHeader,
    HeaderActions,
    TradingCard,
    NewTradingButton,
    FilterTitle,
} from "../../Trading/styles";
import styled from "styled-components";

const ViewBankPageHeader = styled(TradingPageHeader)`
    z-index: 6;
    margin-bottom: 28px;

    @media (min-width: 768px) {
        margin-bottom: 36px;
    }
`;

const ViewBankHeaderActions = styled(HeaderActions)`
    position: relative;
    z-index: 7;
    margin-left: auto;

    @media (max-width: 767px) {
        width: 100%;
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
    }
`;

const ViewBankNarrow = styled.div`
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
    position: relative;
    z-index: 2;
    margin-top: 16px;

    @media (min-width: 576px) {
        margin-top: 20px;
    }

    @media (min-width: 992px) {
        margin-top: 24px;
    }
`;

const ViewBankCard = styled(TradingCard)`
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding: 20px 22px 22px;

    @media (min-width: 768px) {
        padding: 22px 26px 26px;
    }

    @media (min-width: 992px) {
        padding: 24px 28px 28px;
    }
`;

const CardSectionTitle = styled(FilterTitle)`
    margin-bottom: 14px;
`;

const CurrentActionsWrap = styled.div`
    margin-top: 16px;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const CurrentActionsTitle = styled.div`
    color: #f8fafc;
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 4px;
`;

const CurrentActionsText = styled.div`
    color: rgba(255, 255, 255, 0.7);
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 10px;
`;

const CurrentActionsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 767px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 420px) {
        grid-template-columns: 1fr;
    }
`;

const ActionButtonBase = styled(NewTradingButton)`
    background: linear-gradient(135deg, rgba(10, 14, 39, 0.95) 0%, rgba(22, 33, 62, 0.98) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.24);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
    width: 100%;
    justify-content: center;
    min-height: 44px;
    height: auto;
    padding: 10px 14px;
    font-size: 14px;
    line-height: 1.25;
    white-space: normal;
    text-align: center;
    gap: 8px;

    &:hover {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        box-shadow: 0 5px 14px rgba(0, 0, 0, 0.3);
    }

    @media (max-width: 767px) {
        min-height: 42px;
        padding: 9px 12px;
        font-size: 13px;
    }
`;

const HeaderActionButtonBase = styled(NewTradingButton)`
    justify-content: center;
    white-space: nowrap;

    @media (max-width: 767px) {
        width: 100%;
        min-width: 0;
        padding-left: 12px;
        padding-right: 12px;
        font-size: 13px;
    }
`;

const HeaderActionPrimaryButton = styled(HeaderActionButtonBase)`
    background: linear-gradient(135deg, #f29205 0%, #ff6b35 100%);
    box-shadow: 0 4px 14px rgba(242, 146, 5, 0.32);

    &:hover {
        background: linear-gradient(135deg, #f59e0b 0%, #ff7a45 100%);
        box-shadow: 0 6px 20px rgba(242, 146, 5, 0.38);
    }
`;

const HeaderActionNeutralButton = styled(HeaderActionButtonBase)`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #f5f5f5 !important;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.24);
    }
`;

const ActionNeutralButton = styled(ActionButtonBase)`
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #f5f5f5 !important;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.24);
    }
`;

const DeleteBankHeaderButton = styled(ActionButtonBase).attrs({ as: 'button' })`
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%);
    color: #fff !important;
    box-shadow: 0 4px 14px rgba(220, 38, 38, 0.45);
    border: 1px solid #991b1b;

    @media (max-width: 767px) {
        grid-column: 1 / -1;
    }

    &:hover {
        background: linear-gradient(135deg, #f87171 0%, #ef4444 45%, #dc2626 100%);
        box-shadow: 0 6px 20px rgba(220, 38, 38, 0.5);
        border-color: #7f1d1d;
        color: #fff !important;
    }
`;

const BankSwitcherWrap = styled.div`
    position: relative;
    z-index: 8;

    @media (max-width: 767px) {
        width: 100%;
    }
`;

const BankSwitcherButton = styled(HeaderActionButtonBase)`
    background: linear-gradient(135deg, rgba(10, 14, 39, 0.95) 0%, rgba(22, 33, 62, 0.98) 100%);
    border: none;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(0, 0, 0, 0.2);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);

    &:hover {
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45), 0 0 16px rgba(242, 146, 5, 0.25);
    }
`;

const BankSwitcherMenu = styled.div`
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    min-width: 240px;
    background: linear-gradient(135deg, rgba(10, 14, 39, 0.98) 0%, rgba(22, 33, 62, 0.98) 100%);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 12px;
    box-shadow: 0 14px 34px rgba(0, 0, 0, 0.4);
    overflow: hidden;
    z-index: 30;

    @media (max-width: 767px) {
        left: 0;
        right: auto;
        width: 100%;
        min-width: 0;
    }
`;

const BankSwitcherItem = styled.button`
    width: 100%;
    background: transparent;
    border: none;
    color: #e5e7eb;
    text-align: left;
    padding: 12px 14px;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: #ffffff;
    }
`;

const BankSwitcherEmpty = styled.div`
    padding: 12px 14px;
    color: rgba(255, 255, 255, 0.68);
    font-size: 14px;
`;

export default function ViewBank() {
    const { t } = useTranslation();
    const [deleteBank, setDeleteBank] = useState(false);
    const [banks, setBanks] = useState([]);
    const [showBankSwitcher, setShowBankSwitcher] = useState(false);
    const {broker, initialValue, amount, coinType, investmentRate} = useSelector((state) => state.BankReducer);
    const currentBrokerLabel = getBrokerLabel(broker, t);
    const otherBanksCount = Array.isArray(banks) ? banks.length : 0;

    const initialNum = parseFloat(initialValue != null ? initialValue : 0);
    const amountNum = parseFloat(amount != null ? amount : 0);

    const currentValueColor = useMemo(() => {
        if (amountNum > initialNum) return "#8aedab";
        if (amountNum < initialNum) return "#ff9a9a";
        return "#e0e0e0";
    }, [amountNum, initialNum]);

    const currentAccent = useMemo(() => {
        if (amountNum > initialNum) return "rgba(91, 211, 124, 0.85)";
        if (amountNum < initialNum) return "rgba(255, 107, 107, 0.85)";
        return "rgba(0, 212, 255, 0.55)";
    }, [amountNum, initialNum]);

    const investmentAmount =
        investmentRate > 0 && amount ? parseFloat(investmentRate) * amountNum / 100 : 0;

    useEffect(() => {
        getBanks().then((list) => {
            setBanks(Array.isArray(list) ? list : []);
        });
    }, []);

    function handleSelectBank(bankId) {
        setDefaultBank(bankId).then(() => {
            setShowBankSwitcher(false);
            window.location.href = '/bank/view';
        });
    }

    return (
        <TradingWrapper>
            <DivDiagonal />
            <ContentArea>
                <ViewBankPageHeader>
                    <h1>
                        <i className="fas fa-wallet" aria-hidden="true"/> {t('header_bank')}
                    </h1>
                    <ViewBankHeaderActions>
                        <BankSwitcherWrap>
                            <BankSwitcherButton
                                type="button"
                                onClick={() => setShowBankSwitcher((value) => !value)}
                            >
                                <i className="fas fa-random"/> {t('header_select_bank')}
                            </BankSwitcherButton>
                            {showBankSwitcher && (
                                <BankSwitcherMenu>
                                    {banks.length ? banks.map((bankItem) => (
                                        <BankSwitcherItem
                                            key={bankItem.id}
                                            type="button"
                                            onClick={() => handleSelectBank(bankItem.id)}
                                        >
                                            {getBrokerLabel(bankItem.broker, t)}
                                        </BankSwitcherItem>
                                    )) : (
                                        <BankSwitcherEmpty>
                                            {t('bank_switch_empty')}
                                        </BankSwitcherEmpty>
                                    )}
                                </BankSwitcherMenu>
                            )}
                        </BankSwitcherWrap>
                        <HeaderActionPrimaryButton as={Link} to="/bank/new">
                            <i className="fas fa-plus"/> {t('header_add_bank')}
                        </HeaderActionPrimaryButton>
                    </ViewBankHeaderActions>
                </ViewBankPageHeader>

                <ViewBankNarrow>
                    <ViewBankCard>
                        <CardSectionTitle>
                            <i className="fas fa-info-circle" aria-hidden="true"/> {t('bank_text_info_current')}
                        </CardSectionTitle>
                        <StatsIntro>{t('bank_view_intro')}</StatsIntro>
                        <StatStack>
                            <StatItem $accent="rgba(242, 146, 5, 0.9)">
                                <StatIconWrap
                                    $bg="rgba(242, 146, 5, 0.18)"
                                    $fg="#ffb347"
                                    $shadow="rgba(242, 146, 5, 0.25)"
                                >
                                    <FaStore aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('bank_broker')}</StatLabel>
                                    <StatHint>{t('bank_view_hint_broker')}</StatHint>
                                </StatText>
                                <StatValue $c="#f5f0e6">
                                    {getBrokerLabel(broker, t)}
                                </StatValue>
                            </StatItem>

                            <StatItem $accent="rgba(0, 212, 255, 0.55)">
                                <StatIconWrap
                                    $bg="rgba(0, 212, 255, 0.12)"
                                    $fg="#7ae8ff"
                                    $shadow="rgba(0, 212, 255, 0.18)"
                                >
                                    <FaHourglassStart aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('bank_initial_value')}</StatLabel>
                                    <StatHint>{t('bank_view_hint_initial')}</StatHint>
                                </StatText>
                                <StatValue $c="#e0e0e0">
                                    {coinType} {currencyFormat(initialNum)}
                                </StatValue>
                            </StatItem>

                            <StatItem $accent={currentAccent}>
                                <StatIconWrap
                                    $bg={
                                        amountNum > initialNum
                                            ? "rgba(91, 211, 124, 0.15)"
                                            : amountNum < initialNum
                                              ? "rgba(255, 107, 107, 0.15)"
                                              : "rgba(0, 212, 255, 0.12)"
                                    }
                                    $fg={
                                        amountNum > initialNum
                                            ? "#8aedab"
                                            : amountNum < initialNum
                                              ? "#ff9a9a"
                                              : "#7ae8ff"
                                    }
                                    $shadow={
                                        amountNum > initialNum
                                            ? "rgba(91, 211, 124, 0.2)"
                                            : amountNum < initialNum
                                              ? "rgba(255, 107, 107, 0.2)"
                                              : "rgba(0, 212, 255, 0.18)"
                                    }
                                >
                                    <FaWallet aria-hidden="true"/>
                                </StatIconWrap>
                                <StatText>
                                    <StatLabel>{t('bank_current_value')}</StatLabel>
                                    <StatHint>{t('bank_view_hint_current')}</StatHint>
                                </StatText>
                                <StatValue $c={currentValueColor}>
                                    {coinType} {currencyFormat(amountNum)}
                                </StatValue>
                            </StatItem>

                            {investmentRate > 0 && amount ? (
                                <>
                                    <StatItem $accent="rgba(167, 139, 250, 0.75)">
                                        <StatIconWrap
                                            $bg="rgba(167, 139, 250, 0.15)"
                                            $fg="#c4b5fd"
                                            $shadow="rgba(167, 139, 250, 0.2)"
                                        >
                                            <FaPercent aria-hidden="true"/>
                                        </StatIconWrap>
                                        <StatText>
                                            <StatLabel>{t('bank_investment_rate')}</StatLabel>
                                            <StatHint>{t('bank_view_hint_rate')}</StatHint>
                                        </StatText>
                                        <StatValue $c="#e0e0e0">
                                            {parseFloat(investmentRate).toFixed(2)}%
                                        </StatValue>
                                    </StatItem>
                                    <StatItem $accent="rgba(251, 191, 36, 0.65)">
                                        <StatIconWrap
                                            $bg="rgba(251, 191, 36, 0.14)"
                                            $fg="#fcd34d"
                                            $shadow="rgba(251, 191, 36, 0.2)"
                                        >
                                            <FaCoins aria-hidden="true"/>
                                        </StatIconWrap>
                                        <StatText>
                                            <StatLabel>{t('bank_investment_value')}</StatLabel>
                                            <StatHint>{t('bank_view_hint_investment')}</StatHint>
                                        </StatText>
                                        <StatValue $c="#fde68a">
                                            {coinType} {currencyFormat(investmentAmount)}
                                        </StatValue>
                                    </StatItem>
                                </>
                            ) : null}
                        </StatStack>
                        <CurrentActionsWrap>
                            <CurrentActionsTitle>{t('bank_management_current_actions')}</CurrentActionsTitle>
                            <CurrentActionsText>
                                {t('bank_management_current_actions_hint')}
                            </CurrentActionsText>
                            <CurrentActionsGrid>
                                <ActionButtonBase as={Link} to="/bank/movements">
                                    <i className="fas fa-exchange-alt"/> {t('navbar_moviments')}
                                </ActionButtonBase>
                                <ActionNeutralButton as={Link} to="/bank">
                                    <i className="fas fa-edit"/> {t('edit')}
                                </ActionNeutralButton>
                                <DeleteBankHeaderButton type="button" onClick={() => setDeleteBank(true)}>
                                    <i className="fa fa-trash" aria-hidden="true"/> {t('profile_btn_delete_bank')}
                                </DeleteBankHeaderButton>
                            </CurrentActionsGrid>
                        </CurrentActionsWrap>
                    </ViewBankCard>
                </ViewBankNarrow>
                {deleteBank ? (
                    <AlertDelete
                        functionCallbackCancel={() => setDeleteBank(false)}
                        functionCallbackConfirm={() => { window.location = '/'; }}
                        text={t('alert_remove_bank', { broker: currentBrokerLabel })}
                        urlDelete={process.env.REACT_APP_API_ENDPOINT + `/api/bank/delete`}
                    />
                ) : null}
            </ContentArea>
        </TradingWrapper>
    );
}
